import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { QuotaWidget } from "@/components/QuotaWidget";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PIPELINE_COLUMNS } from "@/lib/statuses";
import { NAF_CATALOG } from "@/lib/nafCatalog";
import { CalendarCheck, Link2, Unlink, AlarmClock, Copy, RefreshCw } from "lucide-react";

const NAF_LABELS = NAF_CATALOG.reduce((acc, n) => ({ ...acc, [n.code]: n.label }), {});

const Kpi = ({ label, value, accent, testId }) => (
  <Card className="p-4 sm:p-5 bg-white border-brand-line rounded-md shadow-card" data-testid={testId}>
    <div className="eyebrow truncate">{label}</div>
    <div className={`mt-2 font-display text-2xl sm:text-3xl leading-none ${accent ? "text-brand-moss" : "text-brand-ink"}`}>{value}</div>
  </Card>
);

export default function StatsPage() {
  const [kpis, setKpis] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [google, setGoogle] = useState(null);
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [overdue, setOverdue] = useState({ count: 0, rappels: 0, rdv: 0 });
  const [params, setParams] = useSearchParams();

  const load = () => {
    axios.get(`${API}/kpis`).then((r) => setKpis(r.data)).catch(() => {});
    axios.get(`${API}/pipeline`).then((r) => setPipeline(r.data)).catch(() => {});
    axios.get(`${API}/google/calendar/status`).then((r) => setGoogle(r.data)).catch(() => {});
    axios.get(`${API}/events`).then((r) => setEvents(r.data.results || [])).catch(() => {});
    axios.get(`${API}/analytics/conversion`).then((r) => setAnalytics(r.data)).catch(() => {});
    axios.get(`${API}/events/overdue`).then((r) => setOverdue(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const flag = params.get("google");
    if (flag === "connecte") toast.success("Google Calendar connecté");
    if (flag === "erreur") toast.error("Connexion Google Calendar échouée");
    if (flag) { params.delete("google"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGoogle = async () => {
    try {
      const r = await axios.get(`${API}/google/calendar/auth-url`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Identifiants OAuth Google manquants");
    }
  };

  const disconnectGoogle = async () => {
    await axios.post(`${API}/google/calendar/disconnect`);
    toast.success("Google Calendar déconnecté");
    load();
  };

  const syncPending = async () => {
    try {
      const r = await axios.post(`${API}/google/calendar/sync-pending`);
      toast[r.data.synced ? "success" : "info"](
        r.data.synced
          ? `${r.data.synced} RDV synchronisé(s) avec Google Calendar`
          : "Google Calendar n'a pas accepté la synchronisation, réessayez"
      );
      load();
    } catch { toast.error("Synchronisation impossible"); }
  };

  const rdvCount = events.filter((e) => e.type === "rdv").length;
  const rappelCount = events.filter((e) => e.type === "rappel").length;
  const pendingSync = events.filter((e) => e.type === "rdv" && e.google_sync_status !== "synchronise").length;
  const maxCol = pipeline ? Math.max(...PIPELINE_COLUMNS.map((c) => c.statuses.reduce((s, st) => s + (pipeline.by_status[st] || 0), 0)), 1) : 1;

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="mb-8">
          <div className="eyebrow">Performance</div>
          <h1 className="font-display text-3xl sm:text-4xl text-brand-ink mt-2 leading-none">Statistiques</h1>
        </div>

        {kpis && pipeline && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Kpi label="Entreprises identifiées" value={kpis.total} testId="stats-total" />
            <Kpi label="Enrichi Google" value={`${kpis.enriched_rate}%`} testId="stats-enriched" />
            <Kpi label="RDV pris" value={pipeline.rdv_pris} accent testId="stats-rdv" />
            <Kpi label="Taux de conversion" value={`${pipeline.conversion_rate}%`} accent testId="stats-conversion" />
          </div>
        )}

        {pipeline && (
          <Card className="p-5 sm:p-8 mb-6 bg-white border-brand-line rounded-md shadow-card" data-testid="stats-funnel">
            <div className="eyebrow mb-1">Entonnoir de prospection</div>
            <div className="font-display text-xl sm:text-2xl text-brand-ink mb-7">
              {pipeline.total} prospects → {pipeline.contacted} appelés → {pipeline.rdv_pris} RDV
            </div>
            <div className="space-y-5">
              {PIPELINE_COLUMNS.map((c) => {
                const count = c.statuses.reduce((s, st) => s + (pipeline.by_status[st] || 0), 0);
                return (
                  <div key={c.id}>
                    <div className="flex items-baseline justify-between text-xs mb-2">
                      <span className="eyebrow">{c.label}</span>
                      <span className="font-mono text-brand-ink">{count}</span>
                    </div>
                    <div className="h-[3px] bg-brand-line overflow-hidden">
                      <div className="h-full bg-brand-moss transition-[width] duration-500" style={{ width: `${(count / maxCol) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {pipeline && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Kpi label="Prospects contactés" value={pipeline.contacted} testId="stats-contacted" />
            <Kpi label="Intéressés" value={pipeline.by_status.interesse} testId="stats-interesses" />
            <Kpi label="Rappels planifiés" value={rappelCount} testId="stats-rappels" />
            <Card className={`p-4 sm:p-5 bg-white rounded-md shadow-card ${overdue.count ? "border-[#EBD5CB]" : "border-brand-line"}`} data-testid="stats-overdue">
              <div className="eyebrow flex items-center gap-2">
                {overdue.count > 0 && <AlarmClock className="h-3.5 w-3.5 text-[#8A3F27]" strokeWidth={1.75} />} En retard
              </div>
              <div className={`mt-2 font-display text-2xl sm:text-3xl leading-none ${overdue.count ? "text-[#8A3F27]" : "text-brand-ink"}`}>
                {overdue.count}
              </div>
            </Card>
          </div>
        )}

        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
            {[
              { title: "Conversion par métier", rows: analytics.by_naf, testId: "conversion-by-naf", keyLabel: "Code NAF" },
              { title: "Conversion par département", rows: analytics.by_departement, testId: "conversion-by-dept", keyLabel: "Département" },
            ].map((block) => {
              const best = Math.max(...(block.rows.length ? block.rows.map((r) => r.conversion_rate) : [1]), 1);
              return (
                <Card key={block.testId} className="p-5 sm:p-6 bg-white border-brand-line rounded-md shadow-card" data-testid={block.testId}>
                  <div className="eyebrow">{block.keyLabel}</div>
                  <div className="font-display text-xl sm:text-2xl text-brand-ink mt-2 mb-6">{block.title}</div>

                  {block.rows.length === 0 && (
                    <div className="text-xs text-brand-subtle py-6">Pas encore assez de données. Lancez des appels pour alimenter ces statistiques.</div>
                  )}

                  <div className="space-y-4">
                    {block.rows.map((r) => (
                      <div key={r.key} data-testid={`${block.testId}-row-${r.key}`}>
                        <div className="flex items-baseline justify-between gap-3 mb-1.5">
                          <span className="text-xs text-brand-ink truncate">
                            <span className="font-mono text-[11px] text-brand-subtle mr-2">{r.key}</span>
                            {block.testId === "conversion-by-naf"
                              ? (r.label !== r.key ? r.label : NAF_LABELS[r.key] || "")
                              : (r.label !== r.key ? r.label : "")}
                          </span>
                          <span className="font-mono text-[11px] text-brand-subtle shrink-0">
                            {r.rdv} RDV / {r.contacted} appelés
                            <span className={`ml-2 ${r.conversion_rate > 0 ? "text-brand-moss" : ""}`}>{r.conversion_rate}%</span>
                          </span>
                        </div>
                        <div className="h-[3px] bg-brand-line overflow-hidden">
                          <div className="h-full bg-brand-moss transition-[width] duration-500"
                            style={{ width: `${(r.conversion_rate / best) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <QuotaWidget />

          <Card className="p-5 sm:p-6 bg-white border-brand-line rounded-md shadow-card" data-testid="google-card">
            <div className="eyebrow">Agenda</div>
            <div className="font-display text-xl sm:text-2xl text-brand-ink mt-2 mb-5">Google Calendar</div>

            <div className="space-y-3 text-sm text-brand-ink/85">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Statut</span>
                <span className="font-mono text-xs" data-testid="google-status">
                  {google?.connected
                    ? `Connecté · ${google.email}`
                    : google?.token_expired
                    ? `Token expiré · ${google.email || "reconnexion requise"}`
                    : google?.configured
                    ? "Non connecté"
                    : "Identifiants OAuth manquants"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="eyebrow">RDV Studio</span>
                <span className="font-mono text-xs">{rdvCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="eyebrow">Rappels Studio</span>
                <span className="font-mono text-xs">{rappelCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="eyebrow">Synchro en attente</span>
                <span className={`font-mono text-xs ${pendingSync ? "text-[#6B5B33]" : "text-brand-moss"}`}>{pendingSync}</span>
              </div>
            </div>

            <p className="mt-5 text-[11px] text-brand-subtle leading-relaxed">
              Sens unique : les RDV créés dans Studio sont poussés vers votre Google Calendar.
              Aucun événement Google n'est importé, et les rappels restent uniquement dans Studio.
            </p>

            {!google?.connected && google?.redirect_uri && (
              <div className="mt-5 rounded-md border border-brand-line bg-brand-paper p-4" data-testid="google-redirect-help">
                <div className="eyebrow mb-2">À enregistrer dans Google Cloud Console</div>
                <p className="text-[11px] text-brand-subtle leading-relaxed mb-3">
                  Credentials → votre client OAuth → « URI de redirection autorisés » : collez exactement cette valeur
                  (une erreur <span className="font-mono">redirect_uri_mismatch</span> signifie qu'elle est absente ou différente).
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 min-w-0 truncate font-mono text-[11px] bg-white border border-brand-line rounded px-2 py-1.5" data-testid="google-redirect-uri">
                    {google.redirect_uri}
                  </code>
                  <Button size="sm" variant="outline" data-testid="google-copy-redirect"
                    onClick={() => { navigator.clipboard?.writeText(google.redirect_uri); toast.success("URI de redirection copiée"); }}
                    className="border-brand-line rounded-md hover:bg-white shrink-0">
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </Button>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 min-w-0 truncate font-mono text-[11px] bg-white border border-brand-line rounded px-2 py-1.5" data-testid="google-js-origin">
                    {google.javascript_origin}
                  </code>
                  <span className="eyebrow shrink-0">Origine JS</span>
                </div>
                <p className="mt-3 text-[11px] text-brand-subtle leading-relaxed" data-testid="google-audience-hint">
                  Erreur <span className="font-mono">403 org_internal</span> ? Dans Google Auth Platform → Audience,
                  passez l'application en <strong className="text-brand-ink">Externe</strong> et ajoutez votre adresse
                  Gmail dans « Utilisateurs tests » : une audience « Interne » est réservée aux comptes Workspace.
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              {google?.connected ? (
                <>
                  <Button size="sm" variant="outline" onClick={disconnectGoogle} data-testid="google-disconnect"
                    className="border-brand-line rounded-md hover:bg-brand-paper">
                    <Unlink className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Déconnecter
                  </Button>
                  {pendingSync > 0 && (
                    <Button size="sm" onClick={syncPending} data-testid="google-sync-pending"
                      className="bg-brand-moss hover:bg-brand-moss2 text-white rounded-md">
                      <RefreshCw className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Synchroniser {pendingSync} RDV
                    </Button>
                  )}
                </>
              ) : (
                <Button size="sm" onClick={connectGoogle} data-testid="google-connect"
                  className="bg-brand-moss hover:bg-brand-moss2 text-white rounded-md">
                  <Link2 className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} />
                  {google?.token_expired ? "Reconnecter Google Calendar" : "Connecter Google Calendar"}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={load} data-testid="stats-refresh"
                className="border-brand-line rounded-md hover:bg-brand-paper">
                <CalendarCheck className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Rafraîchir
              </Button>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
